import { Hidden, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import { useSetHeaderBack, useSetHeaderTitle } from 'shared/hooks/useSetHeader';
import { Badge, Body, HeaderCont, MainCont, MenuButton, MobileMenuCont } from './Audition.styles';
import { useAuditionData } from './useAuditionData';

const AuditionRequests = React.lazy(() => import('../../components/Audition/AuditionRequests/AuditionRequests'));
const UpcomingAuditions = React.lazy(() => import('../../components/Audition/UpcomingAuditions/UpcomingAuditions'));
const AuditionHistory = React.lazy(() => import('../../components/Audition/AuditionHistory/AuditionHistory'));
const SelfTapes = React.lazy(() => import('../../components/Audition/SelfTapes/SelfTapes'));
const AuditionRequestDetails = React.lazy(() => import('../AuditionRequest/AuditionRequestDetails'));

type IButtonKey = 'audition_request' | 'upcoming_auditions' | 'self_tapes' | 'audition_history';

const Audition = () => {
  const { tab, audition_id } = useParams() as { tab: IButtonKey; audition_id: string };
  useSetHeaderBack(false);
  useSetHeaderTitle('Audition');
  const history = useHistory();
  const [activeButton, setActiveButton] = useState<IButtonKey>('audition_request');
  const {
    auditionRequest,
    declinedAudition,
    completedAudition,
    upcomingAudition,
    upcomingSelfTapes,
    audReqLoading,
    upcomingSelfTapesLoading,
    upAudLoading,
    pastSelftapes,
    pastSTLoading,
    declinedLoading,
    completedLoading,
    declinedST,
    isDeclineSTLoading,
    pendingAudition,
    pendingLoading,
    noResponseSelfTapes,
    noResponseSelfTapesDataLoading,
    noResponseStudioAuditionDataLoading,
    noResponseStudioAudition,
  } = useAuditionData();

  const isLoading =
    audReqLoading ||
    upcomingSelfTapesLoading ||
    upAudLoading ||
    pastSTLoading ||
    declinedLoading ||
    completedLoading ||
    isDeclineSTLoading ||
    pendingLoading;

  const filteredCompleted = useMemo(
    () => (completedAudition || []).filter((f) => f.phase !== 'Self Tapes Request'),
    [completedAudition],
  );
  const filteredDeclined = useMemo(
    () => (declinedAudition || []).filter((f) => f.phase !== 'Self Tapes Request'),
    [declinedAudition],
  );

  const buttonArr = useMemo(
    () => [
      { key: 'audition_request', name: 'Audition Requests', badge: (auditionRequest || []).length },
      { key: 'upcoming_auditions', name: 'Upcoming Auditions', badge: (upcomingAudition || []).length },
      {
        key: 'self_tapes',
        name: 'Self Tapes',
        badge: (upcomingSelfTapes || []).length + (pendingAudition || []).length,
      },
      {
        key: 'audition_history',
        name: 'Audition History',
        badge:
          filteredCompleted.length +
          filteredDeclined.length +
          (pastSelftapes || []).length +
          (declinedST || []).length +
          (noResponseSelfTapes || []).length +
          (noResponseStudioAudition || []).length,
      },
    ],
    [
      auditionRequest,
      upcomingAudition,
      upcomingSelfTapes,
      pendingAudition,
      filteredCompleted,
      pastSelftapes,
      noResponseSelfTapes,
      noResponseStudioAudition,
    ],
  );

  useEffect(() => {
    setActiveButton(tab);
  }, [tab]);

  const handleClick = useCallback((key: IButtonKey) => {
    history.push(`${ROUTES.TALENT.AUDITIONS}/${key}`);
  }, []);

  const renderBody = () => {
    switch (activeButton) {
      case 'audition_request':
        return <AuditionRequests data={auditionRequest || []} isLoading={isLoading} />;
      case 'upcoming_auditions':
        return <UpcomingAuditions data={upcomingAudition || []} isLoading={isLoading} />;
      case 'audition_history':
        return (
          <AuditionHistory
            isLoading={isLoading}
            confirmed={filteredCompleted}
            declined={filteredDeclined}
            pastSelftapes={pastSelftapes || []}
            declinedST={declinedST || []}
            noResponseSelfTapes={noResponseSelfTapes || []}
            noResponseSelfTapesLoading={noResponseSelfTapesDataLoading}
            noResponseStudioAudition={noResponseStudioAudition || []}
            noResponseStudioAuditionDataLoading={noResponseStudioAuditionDataLoading}
          />
        );
      case 'self_tapes':
        return (
          <SelfTapes
            pending={pendingAudition || []}
            data={upcomingSelfTapes || []}
            isLoading={isLoading}
            isPendingLoading={isLoading}
          />
        );
      default:
        break;
    }
  };

  const mobileHeader = useMemo(() => buttonArr.find((f) => f.key === activeButton), []);

  return audition_id ? (
    <AuditionRequestDetails auditionScheduleId={audition_id} />
  ) : (
    <MainCont>
      <Hidden smDown>
        <div>
          {buttonArr.map(({ key, name, badge }) => (
            <MenuButton active={key === activeButton} onClick={() => handleClick(key as IButtonKey)}>
              <Typography>{name}</Typography>
              <Badge disabled={badge === 0}>
                <Typography>{badge}</Typography>
              </Badge>
            </MenuButton>
          ))}
        </div>
      </Hidden>
      <Hidden mdUp>
        <MobileMenuCont>
          <HeaderCont className="collapse" expandIcon={<ExpandMoreIcon />}>
            <MenuButton
              style={{ marginBottom: 0 }}
              disabled={false}
              active={mobileHeader!.key === activeButton}
              onClick={() => handleClick(mobileHeader!.key as IButtonKey)}
            >
              <Typography>{mobileHeader!.name}</Typography>
              <Badge disabled={mobileHeader!.badge === 0}>
                <Typography>{mobileHeader!.badge}</Typography>
              </Badge>
            </MenuButton>
          </HeaderCont>
          <Body>
            <div>
              {buttonArr
                .filter((f) => f.key !== activeButton)
                .map(({ key, name, badge }) => (
                  <MenuButton
                    disabled={false}
                    active={key === activeButton}
                    onClick={() => handleClick(key as IButtonKey)}
                  >
                    <Typography>{name}</Typography>
                    <Badge disabled={badge === 0}>
                      <p style={{ fontSize: 16 }}>{badge}</p>
                    </Badge>
                  </MenuButton>
                ))}
            </div>
          </Body>
        </MobileMenuCont>
      </Hidden>
      {renderBody()}
    </MainCont>
  );
};

export default Audition;
